import { useState, useEffect, useMemo } from 'react';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

// Use Cases
import { searchInColumnFilters } from './tableColumnManagementUseCase';

/**
 * @typedef useTableColumnManagementReturn
 * @property {ApiManagerTableFiltersColumnFilters[]} columnFiltersLocal
 * @property {Array<{lib: string, options: Array<ApiManagerTableFiltersColumnFilters>}>} tableColumnsHiddenGroups
 * @property {string[]} columnsPinnedById - The columns pinned by id
 * @property {(id: string, columnsPreference: Partial<ApiManagerTableFiltersColumnFilters>) => void} handleSetColumnPreference
 * @property {(columnsFiltersReordered: Array<ApiManagerTableFiltersColumnFilters>) => void} handleSetColumnsOrder
 * @property {(groupColumns: Partial<ApiManagerTableFiltersColumnFilters>[], columnsPreference: Partial<ApiManagerTableFiltersColumnFilters>) => void} handleSetColumnPreferenceGroup
 * @property {() => void} handleCancelColumnsManagement
 * @property {string} searchHiddenGroups
 * @property {(search: string) => void} setSearchHiddenGroups
 * @property {boolean} popoverColumnsManagementIsOpen
 * @property {(isOpen: boolean) => void} setPopoverColumnsManagementIsOpen
 */

/**
 * Use table column management
 * @param {object} props - The props
 * @param {Array<ApiManagerTableFiltersColumnFilters>} props.columnFilters - The columns filters
 * @param {boolean} props.isLoading - The loading state
 * @returns {useTableColumnManagementReturn} The table column management
 */

const useTableColumnManagement = (
  {
    columnFilters,
    isLoading,
  },
) => {
  const [popoverColumnsManagementIsOpen, setPopoverColumnsManagementIsOpen] = useState(false);
  const [searchHiddenGroups, setSearchHiddenGroups] = useState('');

  // Columns preferences - all columns with visible and pinned
  /** @type {[ApiManagerTableFiltersColumnFilters[], React.Dispatch<React.SetStateAction<ApiManagerTableFiltersColumnFilters[]>>]} */
  const [columnFiltersLocal, setColumnFiltersLocal] = useState([]);

  useEffect(() => {
    if (!isLoading && Array.isArray(columnFilters)) {
      // Réinitialiser columnFiltersLocal pour éviter d'afficher les colonnes de la page précédente
      setColumnFiltersLocal(columnFilters);
    }
  }, [isLoading, columnFilters]);

  /**
   * Handle set column filter
   * @param {string} id - The id
   * @param {Partial<ApiManagerTableFiltersColumnFilters>} columnsPreference - The columns preference
   */
  const handleSetColumnPreference = (id, columnsPreference) => {
    setColumnFiltersLocal((prev) => prev.map((columnFilter) => {
      if (columnFilter.id === id) {
        return { ...columnFilter, ...columnsPreference };
      }
      return columnFilter;
    }));
  };

  /**
   * Handle set columns preferences
   * @param {Array<ApiManagerTableFiltersColumnFilters>} columnsFiltersReordered - The columns filters reordered
   */
  const handleSetColumnsOrder = (columnsFiltersReordered) => {
    setColumnFiltersLocal(columnsFiltersReordered);
  };

  /**
   * Handle set columns preferences group
   * @param {Array<ApiManagerTableFiltersColumnFilters>} groupColumns - The columns
   * @param {Partial<ApiManagerTableFiltersColumnFilters>} columnsPreference - The columns preference group
   */
  const handleSetColumnPreferenceGroup = (groupColumns, columnsPreference) => {
    groupColumns.forEach((column) => {
      handleSetColumnPreference(column.id, columnsPreference);
    });
  };

  /**
   * Handle cancel columns management
   */
  const handleCancelColumnsManagement = () => {
    setPopoverColumnsManagementIsOpen(false);
    setColumnFiltersLocal(columnFilters ?? []);
  };

  /**
   * @description The table columns hidden groups generated for columns management
   */
  const tableColumnsHiddenGroups = useMemo(
    () => {
      if (!columnFiltersLocal?.length || isLoading) return [];
      const hiddenColumns = columnFiltersLocal.filter((columnFilter) => !columnFilter.visible);
      const hiddenColumnsSearched = searchInColumnFilters(hiddenColumns, searchHiddenGroups);
      return groupOptionsByOptGroup(hiddenColumnsSearched);
    },
    [columnFiltersLocal, searchHiddenGroups, isLoading],
  );

  /**
   * @description The columns pinned by id
   * @type {string[]}
   */
  const columnsPinnedById = useMemo(
    () => {
      if (!columnFilters?.length || isLoading) return [];
      return columnFilters.filter((columnFilter) => columnFilter.pinned).map((columnFilter) => columnFilter.id);
    },
    [columnFilters, isLoading],
  );

  return {
    columnFiltersLocal,
    tableColumnsHiddenGroups,
    columnsPinnedById,

    // Table column management - Actions
    handleSetColumnPreference,
    handleSetColumnsOrder,
    handleSetColumnPreferenceGroup,

    // Table column management - Search Column management
    searchHiddenGroups,
    setSearchHiddenGroups,

    // Table column management - Columns preferences
    handleCancelColumnsManagement,

    // Table column management - Popover
    popoverColumnsManagementIsOpen,
    setPopoverColumnsManagementIsOpen,
  };
};

export default useTableColumnManagement;
